import React, { useEffect, useMemo, useState } from 'react';
import styles from './TipPublishForm.module.scss';

/* Redux */
import { connect } from 'react-redux';

/* Router */
import { RouteComponentProps, withRouter } from 'react-router';

/* Components */

//Layout Components
import { Button } from './shared/Button/Button';
//steps
import { IStep, Steps, allSteps } from './Steps/Steps';
import { StepOne } from './Steps/StepOne';
import { StepTwo } from './Steps/StepTwo';
import { StepThree } from './Steps/StepThree';
import { StepFour } from './Steps/StepFour';

/* Utils */
//tr
import { tr } from '../../utils/translations/translator';
import { useTranslation } from 'react-i18next';
// redux
import { RootState } from '../../store';
import { BookiesState } from '../../store/bookies/reducer';
import { MarketsState } from '../../store/markets/reducer';
import { SportsState } from '../../store/sports/reducer';
import { Dispatch } from 'redux';
import { tipsActions } from '../../store/tips/actions';
import { marketsActions } from '../../store/markets/actions';
import { bookiesActions } from '../../store/bookies/actions';
import { sportsActions } from '../../store/sports/actions';
import { LoginState } from '../../store/login/reducer';
import { TipsInfoState } from '../../store/tips/reducer';
//other
import { useTipPublish } from './__hooks__/useTipPublish';
import { Channel, TipType } from './interfaces/tip/Tip';
import { get } from 'lodash';
import { CredentialsUserObject } from '../../types/Login';
import { getMediaFilesIDs } from './utils/DTO/functionsDTO';
import { toDTO } from './utils/DTO/tipPublishDTO';
import { toast } from 'react-toastify';
import { betsActions } from '../../store/bets/actions';
import { tipsterPreferencesActions } from '../../store/tipsterPreferences/actions';
import { TipsterPreferencesState } from '../../store/tipsterPreferences/reducer';
import { BounceLoader } from 'react-spinners';
import { CustomToast } from '../../utils/errorHandling/CustomToast';
import { ChannelsState } from '../../store/channels/reducer';
import { channelsActions } from '../../store/channels/actions';
import { useFeatureFlagContext } from '../__providers__/FeatureFlagsContext';

interface TipPublishFormProps extends RouteComponentProps {
  bookiesState: BookiesState;
  marketsState: MarketsState;
  sportsState: SportsState;
  loginState: LoginState;
  tipsInfo: TipsInfoState;
  requestMatchBet: (id: string) => void;
  requestMarkets: () => void;
  requestBookies: () => void;
  requestSports: () => void;
  requestPricingTiers: () => any;
  requestTipsterPreferences: (tipsterID: string) => void;
  tipsterPreferencesState: TipsterPreferencesState;
  createBet: (formData: string) => any;
  channelsState: ChannelsState;
  requestChannels: (tipsterID: string) => any;
}

export const TipPublishForm: React.FC<TipPublishFormProps> = ({
  bookiesState,
  marketsState,
  sportsState,
  tipsInfo,
  requestBookies,
  requestMarkets,
  requestSports,
  requestPricingTiers,
  requestTipsterPreferences,
  tipsterPreferencesState,
  loginState,
  createBet,
  channelsState,
  requestChannels,
}) => {
  const tipsterID = get(loginState, ['user', 'user', 'id']);

  const [channelType, setChannelType] = useState<undefined | Channel>(
    undefined,
  );
  useEffect(() => {
    if (
      !!channelsState.data.length &&
      !channelsState.isLoading &&
      channelsState.requested
    ) {
      const channelTypes = channelsState.data.map((channel) => channel.type);
      const channelType = channelTypes.find((type) =>
        window.location.href.includes(type),
      );

      if (!channelType) {
        return;
      }
      const channel: Channel | undefined = channelsState.data.find(
        (channel) => channelType === channel.type,
      );
      channel && setChannelType(channel);
    }
  }, [channelsState]);

  const activeSteps: IStep[] = useMemo(() => {
    // Step 3 (PRICE) should be hidden for free/public channels so tips are always published as FREE.
    const isFreeOrSinglePriceChannel =
      (channelType && channelType.type === 'PUBLIC_CHANNEL') ||
      (channelType &&
        channelType.publishable_tip_types &&
        channelType.publishable_tip_types.length <= 1);

    if (window.location.href.includes('challenge') || isFreeOrSinglePriceChannel) {
      return ['TIP', 'DETAIL', 'PUBLISH'];
    }

    return ['TIP', 'DETAIL', 'PRICE', 'PUBLISH'];
  }, [channelType]);

  const [selectedStep, setSelectedStep] = useState<IStep>('TIP');
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const { isEnabled } = useFeatureFlagContext();
  const isChannelsFlagEnabled = isEnabled('channel_based_tips_publishing');

  const { t } = useTranslation();
  const tipType: TipType = window.location.pathname.includes('simple')
    ? 'SIMPLE'
    : 'COMBINED';

  const {
    states: { tip },
    handleMatchSelection,
    handleMatchSwitcher,
    handleTipValue,
    handleMatchForecastValue,
    handleFormattedOdds,
    handleMatchForecastReset,
    handleAddMatchForecast,
    setShowValidations,
    showValidations,
    checkStepValidity,
    featuredMarkets,
    featuredMarketsBySport,
    featuredBookies,
    featuredSports,
    featuredStakes,
    areStatesLoading,
    handleEmptyMatches,
  } = useTipPublish({
    marketsState,
    bookiesState,
    requestBookies,
    requestMarkets,
    requestPricingTiers,
    requestTipsterPreferences,
    tipsterPreferencesState,
    tipsterID,
    requestSports,
    sportsState,
    tipsInfo,
    tipType,
    channelType,
    channelsState,
    requestChannels,
  });

  const handleSelectStep = (step: number) => {
    setShowValidations(false);
    const isStepValid = checkStepValidity(allSteps.indexOf(selectedStep));
    const isStepAhead = allSteps.indexOf(selectedStep) < step;
    if (allSteps.indexOf(selectedStep) === 0 && isStepValid) {
      tipType === 'COMBINED' && handleEmptyMatches();
    }
    if (!isStepValid && isStepAhead) {
      setShowValidations(true);
      return;
    }
    setSelectedStep(activeSteps[step]);
  };

  const user: CredentialsUserObject = get(loginState, ['user', 'user']);
  const handleNext = async () => {
    const isStepValid = checkStepValidity(allSteps.indexOf(selectedStep));

    if (isStepValid) {
      if (allSteps.indexOf(selectedStep) === 0) {
        tipType === 'COMBINED' && handleEmptyMatches();
      }
      if (allSteps.indexOf(selectedStep) < 3) {
        setShowValidations(false);
        if (
          selectedStep === 'DETAIL' &&
          window.location.href.includes('challenge')
        ) {
          setSelectedStep('PUBLISH');
        } else {
          handleSelectStep(activeSteps.indexOf(selectedStep) + 1);
        }
      } else {
        setLoadingSubmit(true);
        const finished = await handlePublishTip();

        setTimeout(() => {
          finished && setLoadingSubmit(false);
        }, 2000);
      }
    } else setShowValidations(true);
  };

  const handlePublishTip = async (): Promise<boolean> => {
    try {
      const media_files_ids =
        tip.media.length > 0 ? await getMediaFilesIDs(tip.media) : [];
      const betslip_images_id = await getMediaFilesIDs([tip.betImage!]);

      if (!channelType) {
        const urlParts = window.location.href.split('/');
        const lastPart = urlParts[urlParts.length - 1];

        tip.channel = {
          icon_url:
            'https://cdn.tipsterchat.com/spaces/sections/icons/public_channel_v2.png',
          id: `pub_${lastPart}`,
          publishable_tip_types: ['FREE', 'PAID'],
          title: 'Free\nChannel',
          type: 'PUBLIC_CHANNEL',
        } as Channel;
      } else {
        tip.channel = channelType!;
      }

      if (
        isChannelsFlagEnabled &&
        channelType &&
        channelType.type === 'PREMIUM_CHANNEL'
      ) {
        tip.priceType = 'PREMIUM';
      }
      createBet(toDTO(tip, media_files_ids, betslip_images_id[0]));
    } catch (e: any) {
      console.log(e);
      if (e && e.detail) {
        toast.error(CustomToast(tr(t, 'image-upload_error'), tr(t, e.type)));
      } else {
        toast.error(CustomToast(tr(t, 'image-upload_error'), e.message));
      }
    }
    return true;
  };

  return (
    <div className={styles.TipPublishForm} data-testid="TipPublishForm">
      {areStatesLoading ? (
        <div className={styles.loadingWrapper}>
          <div className={styles.loading}>
            <BounceLoader color={'#27de81'} size={25} />
            <label>{tr(t, 'loading')} ...</label>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.sectionHeader}>
            <div className={styles.text}>
              <label>{tr(t, 'tips')}</label>
              <h1>{tr(t, 'publish-tip')}</h1>
            </div>
            <Steps
              onSelectStep={handleSelectStep}
              checkStepValidity={checkStepValidity}
              selectedStep={selectedStep}
              steps={activeSteps}
            />

            {channelType && (
              <div className={styles.channelBanner}>
                <p>
                  <span>{`${tr(t, tipType.toLowerCase() + '-tip')}`}</span>
                  {` ${tr(t, 'in')} `}
                  <span>{`${tr(t, channelType.type.toLowerCase())}`}</span>
                </p>
              </div>
            )}
          </div>
          <div className={styles.container}>
            {selectedStep === 'TIP' && (
              <StepOne
                hooks={{
                  handleFormattedOdds,
                  handleTipValue,
                  handleMatchSelection,
                  handleMatchSwitcher,
                  handleMatchForecastValue,
                  handleMatchForecastReset,
                  handleAddMatchForecast,
                }}
                bookies={bookiesState.data.bookies}
                markets={marketsState.data.markets}
                marketsBySport={marketsState.data.markets_by_sport}
                sports={sportsState.data.sports}
                tip={tip}
                featuredBookies={featuredBookies}
                featuredMarkets={featuredMarkets}
                featuredMarketsBySport={featuredMarketsBySport}
                featuredSports={featuredSports}
                featuredStakes={featuredStakes}
                oddsFormat={user.odds_format || 'DECIMAL'}
                showValidations={showValidations}
              />
            )}

            {selectedStep === 'DETAIL' && (
              <StepTwo hooks={{ handleTipValue }} tip={tip} />
            )}

            {selectedStep === 'PRICE' && (
              <StepThree
                pricingTiers={tipsInfo.pricing}
                hooks={{ handleTipValue, tip }}
                currency={user.currency}
                showValidations={showValidations}
              />
            )}

            {selectedStep === 'PUBLISH' && (
              <StepFour
                user={loginState.user}
                hooks={{ handleTipValue, tip }}
                showValidations={showValidations}
                oddsFormat={user.odds_format || 'DECIMAL'}
              />
            )}
          </div>
          <div className={styles.nextBtnWrapper}>
            <button
              disabled={loadingSubmit || selectedStep === 'TIP'}
              className={styles.previousBtn}
              onClick={() => {
                handleSelectStep(activeSteps.indexOf(selectedStep) - 1);
              }}
            >
              {tr(t, 'previous')}
            </button>
            <Button
              type="button"
              onClick={() => {
                selectedStep === 'PUBLISH' &&
                  checkStepValidity(allSteps.indexOf(selectedStep));
                handleNext();
              }}
              disabled={loadingSubmit}
              customClass={
                checkStepValidity(allSteps.indexOf(selectedStep))
                  ? styles.unset
                  : styles.disabledBtn
              }
            >
              {selectedStep !== 'PUBLISH' ? tr(t, 'next') : tr(t, 'publish')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  marketsState: state.markets,
  sportsState: state.sports,
  bookiesState: state.bookies,
  tipsInfo: state.tipsInfo,
  loginState: state.login,
  tipsterPreferencesState: state.tipsterPreferences,
  channelsState: state.channels,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  requestPricingTiers: () => dispatch(tipsActions.pricingTiersRequestAction()),
  requestMarkets: () => dispatch(marketsActions.marketsRequestAction()),
  requestBookies: () => dispatch(bookiesActions.bookiesRequestAction()),
  requestSports: () => dispatch(sportsActions.sportsRequestAction()),
  requestTipsterPreferences: (tipsterID: string) =>
    dispatch(
      tipsterPreferencesActions.tipsterPreferencesRequestAction(tipsterID),
    ),
  createBet: (formData: any) =>
    dispatch(betsActions.createBetRequestAction(formData)),
  requestChannels: (tipsterID: string) =>
    dispatch(channelsActions.channelsRequestAction(tipsterID)),
});

export const TipPublishFormConnected = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TipPublishForm),
);
